import { createSelector, createSlice } from '@reduxjs/toolkit';

import { RootState } from '@/redux/create-store';
import { SlicePayload, UntypedObject } from '@/types';
import { UserModel, OrderDataModel } from '@/models';

type AdminStateStatusValue = { success: boolean } | { error: any } | Record<string, never>;
type AdminStatusKey = 'createCustomerError' | 'getOrdersError' | 'createCustomerSuccess';
type AdminStateStatus = { [key in AdminStatusKey]?: AdminStateStatusValue };

export type AdminState = {
  actAsCustomer?: any;
  content: UntypedObject;
  status: AdminStateStatus;
  ordersData: OrderDataModel;
}

export const initialState: AdminState = {
  actAsCustomer: undefined,
  content: {},
  status: {},
  ordersData: {
    orders: [],
    totalPages: 0
  }
};

type ActAsCustomPayload = {
  customer: UserModel;
  changeStore?: boolean;
  finalUrl?: string;
  redirectHome?: boolean;
  t?: (k: string, options?: { ns?: string }) => string;
}

type ConnectFromOtherDomainPayload = {
  customerId: string;
  storeId: string;
  token: string;
}

const admin = createSlice({
  name: 'admin',
  initialState,
  reducers: {
    // ACT AS CUSTOMER
    setActAsCustomer: (state, action: SlicePayload<ActAsCustomPayload>) => {
      state.actAsCustomer = action.payload.customer;
    },

    // eslint-disable-next-line @typescript-eslint/no-empty-function
    setActAsCustomerSuccess: () => {
    },

    // CUSTOMER CREATION
    // eslint-disable-next-line @typescript-eslint/no-empty-function
    createCustomer(_state, _action: SlicePayload<{ customer: Partial<UserModel>, creator: UserModel }>) {
    },

    createCustomerSuccess: (state, action: SlicePayload<{ createdCustomer: UserModel }>) => {
      state.content.createCustomerSuccess = { customer: action.payload.createdCustomer };
    },

    createCustomerError: (state, action: SlicePayload<{ error: any }>) => {
      state.status.createCustomerError = action.payload;
    },

    // CUSTOMER ORDERS
    // eslint-disable-next-line @typescript-eslint/no-empty-function
    getOrders(_state, _action: SlicePayload<{ page: number, sort: any, filters: any }>) {
    },

    getOrdersSuccess: (state, action: SlicePayload<{ ordersData: OrderDataModel }>) => {
      state.ordersData = action.payload.ordersData;
    },

    getOrdersError: (state, action: SlicePayload<{ error: any }>) => {
      state.status.getOrdersError = action.payload;
    },

    // OTHER
    clearAdminStatus: (state, action: SlicePayload<{ name: AdminStatusKey }>) => {
      state.status[action.payload.name] = {};
    },

    // eslint-disable-next-line @typescript-eslint/no-empty-function
    connectFromOtherDomain(_state, _action: SlicePayload<ConnectFromOtherDomainPayload>) {
    },

    // eslint-disable-next-line @typescript-eslint/no-empty-function
    connectFromOtherDomainSuccess(_state, _action: SlicePayload<{ customerId: string }>) {
    },

    // eslint-disable-next-line @typescript-eslint/no-empty-function
    updateFreeStorageUser(_state, _action: SlicePayload<{ userId: string, t: (_k: string) => string }>) {
    }
  }
});

export const {
  setActAsCustomer, setActAsCustomerSuccess,
  createCustomer, createCustomerSuccess, createCustomerError,
  getOrders, getOrdersSuccess, getOrdersError,
  clearAdminStatus,
  connectFromOtherDomain, connectFromOtherDomainSuccess,
  updateFreeStorageUser
} = admin.actions;

export default admin.reducer;

const adminState = (state: RootState) => state.admin;

export const selectIsAdminActAsCustomer = createSelector(
  [adminState],
  (state) => state.actAsCustomer
);
